import CoreIcon from '@uxcore/assets/icons/CoreIcon';
import FolderIcon from '@uxcore/assets/icons/FolderIcon';
import { HRIconBlue } from '@uxcore/assets/icons/HRIconBlue';
import { HRIconGrey } from '@uxcore/assets/icons/HRIconGrey';
import { OffSecIcon, OffSecIconGrey } from '@uxcore/assets/icons/OffSecIcon';
import { PMIcon } from '@uxcore/assets/icons/PMIcon';
import { PMIconGrey } from '@uxcore/assets/icons/PMIconGrey';
import Search from '@uxcore/components/_biases/Search';
import Logos from '@uxcore/components/Logos';
import Spinner from '@uxcore/components/Spinner';
import ToolFooter from '@uxcore/components/ToolFooter';
import biasesLocalization from '@uxcore/data/biases';
import biasesMobile from '@uxcore/data/biasesMobile';
import useUXCoreGlobals, {
  initUseUXCoreGlobals,
} from '@uxcore/hooks/useUXCoreGlobals';
import useUCoreMobile from '@uxcore/hooks/uxcoreMobile';
import { isOffsecEnabled } from '@uxcore/lib/offsec';
import type { TRouter } from '@uxcore/local-types/global';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useState } from 'react';

import type { UXCoreLayoutProps } from './UXCoreLayout.types';

import styles from './UXCoreLayout.module.scss';

const FolderViewLayout = dynamic(
  () => import('@uxcore/layouts/FolderViewLayout'),
  {
    ssr: false,
  },
);
const CoreViewLayout = dynamic(() => import('@uxcore/layouts/CoreViewLayout'), {
  ssr: false,
});

const UXCorePopup = dynamic(() => import('@uxcore/components/UXCorePopup'), {
  ssr: false,
});

const UXCoreSnackbar = dynamic(
  () => import('@uxcore/components/UXCoreSnackbar'),
  {
    ssr: false,
  },
);

const ViewSwitcher = dynamic(
  () => import('@uxcore/components/_biases/ViewSwitcher'),
  {
    ssr: false,
  },
);
const MobileView = dynamic(
  () => import('@uxcore/components/_biases/MobileView'),
  {
    ssr: false,
  },
);

const UXCoreLayout: FC<UXCoreLayoutProps> = ({
  strapiBiases,
  isOpen,
  biasSelected,
  openPodcast,
  setOpenPodcast,
  userInfo,
  setUserInfo,
  blockLanguageSwitcher,
  mounted,
}) => {
  const [{ toggleIsCoreView }, { isCoreView }] = useUXCoreGlobals();
  const [{ toggleIsProductView }, { isProductView }] = useUXCoreGlobals();
  const [
    { toggleIsOffsecView, setUseCase },
    { isOffsecView, lastBaseUseCase },
  ] = useUXCoreGlobals();
  const router = useRouter();
  const { asPath } = router as TRouter;
  const { isUxcoreMobile } = useUCoreMobile()[1];
  const [isLoaded, setIsLoaded] = useState(false);
  const [snackBarText, setSnackBarText] = useState('');
  const [isSwitched, setIsSwitched] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const { locale } = router as TRouter;
  const data = biasesLocalization[locale];
  const { browsingAsProduct, browsingAsHR, browsingAsOffsec } = data;
  const { description } = biasesMobile[locale];

  useEffect(() => {
    if (!mounted) return;

    // Restore the persisted view choices first, then let an explicit URL
    // hash win over them. Reconcile against the restored state, not the
    // (stale) closure values.
    const restored = initUseUXCoreGlobals();
    const hash = window.location.hash;

    if (hash === '#hr' && (restored.isProductView || restored.isOffsecView)) {
      setUseCase('hr');
    }
    if (hash === '#offsec' && !restored.isOffsecView && isOffsecEnabled) {
      toggleIsOffsecView();
    }
  }, [mounted]);

  useEffect(() => {
    setIsLoaded(true);
  }, [router.events, asPath]);

  useEffect(() => {
    if (!mounted) return;

    const localePrefix = router.locale === 'en' ? '' : `/${router.locale}`;

    const basePath = `${localePrefix}/uxcore`;

    const shouldBeHash = isOffsecView ? '#offsec' : isProductView ? '' : '#hr';

    const targetUrl = `${basePath}${shouldBeHash}`;

    const currentUrl = window.location.pathname + window.location.hash;

    if (currentUrl === targetUrl) return;

    window.history.replaceState(null, '', targetUrl);
  }, [mounted, isProductView, isOffsecView, router.locale]);

  useEffect(() => {
    if (isSwitched !== undefined) {
      if (isOffsecView) {
        setSnackBarText(browsingAsOffsec);
      } else if (isProductView) {
        setSnackBarText(browsingAsProduct);
      } else {
        setSnackBarText(browsingAsHR);
      }
    }
  }, [isSwitched, isProductView, isOffsecView, locale]);

  // One click handler for the three vertical Use cases rows. Sets state
  // explicitly via setUseCase so PM/HR/OffSec are mutually exclusive
  // without depending on the toggle semantics of the older actions.
  // Clicking the active OffSec row reverts to lastBaseUseCase — mirror
  // that resolution here so the snackbar pre-set lands on the correct
  // label and the first frame doesn't flash the wrong text.
  const handleUseCaseClick = (target: 'product' | 'hr' | 'offsec') => {
    const resolved =
      target === 'offsec' && isOffsecView ? lastBaseUseCase || 'hr' : target;
    if (resolved === 'product') setSnackBarText(browsingAsProduct);
    else if (resolved === 'hr') setSnackBarText(browsingAsHR);
    else setSnackBarText(browsingAsOffsec);

    setUseCase(target);
    setIsSwitched(prev => !prev);
    handleSnackbarOpening();
  };

  let snackbarTimeout: NodeJS.Timeout;
  const handleSnackbarOpening = () => {
    clearTimeout(snackbarTimeout);

    setShowSnackbar(true);
    snackbarTimeout = setTimeout(() => {
      setShowSnackbar(false);
    }, 2000);
    return () => clearTimeout(snackbarTimeout);
  };

  if (!isLoaded) {
    return <Spinner visible={true} />;
  }
  return (
    <>
      <section
        className={cn(styles.body, {
          [styles.openedModal]: biasSelected,
          [styles.hyLang]: locale === 'hy',
        })}
      >
        {!isUxcoreMobile && (
          <>
            <ViewSwitcher
              isSecondView={isCoreView}
              toggleIsCoreView={toggleIsCoreView}
              defaultVieWIcon={<CoreIcon />}
              secondViewLabel={'folder'}
              secondViewIcon={<FolderIcon />}
              className={styles.viewTypeSwitcher}
              labelViewType
              wide
              dataCy={'core-view-switcher'}
              dataCySecondView={'folder-view-switcher'}
            />
            <div className={styles.useCasesPanel}>
              <p className={styles.useCasesLabel}>Use cases</p>
              <div
                data-cy="switch-product"
                onClick={() => handleUseCaseClick('product')}
                className={cn(styles.useCaseRow, {
                  [styles.active]: isProductView && !isOffsecView,
                })}
              >
                {isProductView && !isOffsecView ? <PMIcon /> : <PMIconGrey />}
                <span>PM</span>
              </div>
              <div
                data-cy="switch-hr"
                onClick={() => handleUseCaseClick('hr')}
                className={cn(styles.useCaseRow, {
                  [styles.active]: !isProductView && !isOffsecView,
                })}
              >
                {!isProductView && !isOffsecView ? (
                  <HRIconBlue />
                ) : (
                  <HRIconGrey />
                )}
                <span>HR</span>
              </div>
              {isOffsecEnabled && (
                <div
                  data-cy="switch-offsec"
                  onClick={() => handleUseCaseClick('offsec')}
                  className={cn(styles.useCaseRow, {
                    [styles.active]: isOffsecView,
                  })}
                >
                  {isOffsecView ? <OffSecIcon /> : <OffSecIconGrey />}
                  <span className={styles.cybersecFull}>Cybersecurity</span>
                  <span className={styles.cybersecShort}>OffSec</span>
                </div>
              )}
            </div>
            {isCoreView && <Search biases={strapiBiases} />}
            {isCoreView && (
              <>
                <CoreViewLayout biases={strapiBiases} />
                {locale !== 'hy' && openPodcast && (
                  <UXCorePopup
                    setOpenPodcast={setOpenPodcast}
                    openPodcast={openPodcast}
                  />
                )}

                <Logos className={styles.Logos} />
              </>
            )}
            {!isCoreView && (
              <FolderViewLayout biases={strapiBiases} isOpen={isOpen} />
            )}
          </>
        )}
        <div className={styles.MobileView}>
          <MobileView
            isSecondView={isProductView}
            toggleIsCoreView={toggleIsProductView}
            defaultViewLabel={'PM'}
            secondViewLabel={'hr'}
            strapiBiases={strapiBiases}
            containerClassName={styles.body}
            setIsSwitched={setIsSwitched}
            isSwitched={isSwitched}
            isOpen={isOpen}
            hrText={'HR'}
            biasSelected={biasSelected}
            handleSnackbarOpening={handleSnackbarOpening}
            description={description}
            userInfo={userInfo}
            setUserInfo={setUserInfo}
            blockLanguageSwitcher={blockLanguageSwitcher}
          />
        </div>
        <ToolFooter page="uxcore" />
        {!!snackBarText && (
          <UXCoreSnackbar
            text={snackBarText}
            showSnackbar={showSnackbar}
            isHy={locale === 'hy'}
          />
        )}
      </section>
    </>
  );
};

export default UXCoreLayout;
